import React, { useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import {
  LandingContainer,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
} from '../home/HomePage.styles';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { Footer } from '../../shared/components/Footer';
import SEO from '../../shared/components/SEO';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import LoginRounded from '@mui/icons-material/LoginRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import CloudSyncRounded from '@mui/icons-material/CloudSyncRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';

// =============================================================================
// COMPANY PAGE (/company)
// -----------------------------------------------------------------------------
// One consistent company page that folds Careers, Support, and News into
// anchored sections (#founder, #careers, #support, #news), replacing the
// separate careers (marketing template) and support (legal-doc template) pages,
// which used different formats. The header Company menu links to
// /company#section, and the page scrolls to the hash on load and on hash
// change. The full newsroom archive still lives at /news, linked from the News
// section.
//
// Page spine (one offer, one audience, one primary action): hero with the
// primary CTA -> proof row -> section nav -> founder (problem to solution) ->
// careers -> support -> news -> FAQ -> the same primary CTA again. Every FAQ
// answer is pulled by id from FAQ_REPOSITORY so this page and the support
// widget can never drift apart.
//
// House style: no em dashes; unified radii (cards 16px, buttons pill).
// =============================================================================

const ACCENT = '#B83B1D';
const INK = '#2E140A';
const CAREERS_EMAIL = 'careers@re-stack.com';
const SUPPORT_EMAIL = 'hi@re-stack.com';

// Section anchors. `founder` is included so the nav covers every section on the
// page rather than only the three the header menu links to.
const SECTION_NAV = [
  { id: 'founder', label: 'Founder' },
  { id: 'careers', label: 'Careers' },
  { id: 'support', label: 'Support' },
  { id: 'news', label: 'News' },
  { id: 'faq', label: 'FAQ' },
];

// Proof row under the hero. Each line restates a commitment already made
// elsewhere on the site: founder-led delivery (the founder section below),
// the 24-hour support reply (the support section below), and the 30-day
// guarantee (FAQ_REPOSITORY, id 'refund'). Nothing new is claimed here.
const PROOF_POINTS = [
  { stat: 'Founder-led', label: 'Every demo, proof run, and support reply comes from Harsh directly.' },
  { stat: 'Within 24 hours', label: 'Support reply time, in PowerPoint, Excel, or Word.' },
  { stat: '30 days', label: 'Money-back guarantee if Restack does not earn its place.' },
];

const supportTopics = [
  { icon: <LoginRounded />, title: 'Account access', body: 'Google or Microsoft sign-in, pairing problems, or getting stuck before the task pane.' },
  { icon: <CreditCardRounded />, title: 'Billing and credits', body: 'Subscription changes, renewals, invoices, or the shared credit pool across PowerPoint, Excel, and Word.' },
  { icon: <CloudSyncRounded />, title: 'Deployment and setup', body: 'Add-in installation, Microsoft 365 deployment across all three apps, and rollout policy questions.' },
  { icon: <EditNoteRounded />, title: 'Working in your files', body: 'Building slides in PowerPoint, models in Excel, or documents in Word, and moving the work between the apps.' },
];

const newsUpdates = [
  { tag: 'Update', date: 'June 2026', title: 'New comparison guides: Restack vs Claude, Rogo, and Hebbia', body: 'Side-by-side breakdowns of where Restack fits next to the tools deal teams already use.' },
  { tag: 'Launch', date: 'March 2026', title: 'Restack is live on Microsoft AppSource', body: 'Certified and listed on Microsoft AppSource, so IT can deploy it across Microsoft 365 from the admin center.' },
];

// The eight questions a firm asks before it talks to us, ordered the way they
// land: what is it, how does it run, will it run on our Office, what does it
// cost, can the team share it, is it safe, what if it does not work out, and
// who do we talk to. Pulled by id so the copy stays identical to the support
// widget's answers.
const COMPANY_FAQ_IDS = [
  'what-is-restack',
  'how-works',
  'powerpoint-versions',
  'pricing',
  'collaboration',
  'security',
  'refund',
  'contact',
];
const COMPANY_FAQ = COMPANY_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean);

// FAQPage structured data, shipped through SEO.js into <head>.
const COMPANY_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: COMPANY_FAQ.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
};

export default function CompanyPage() {
  const location = useLocation();
  const hair = alpha(INK, 0.1);
  const activeHash = (location.hash || '').replace('#', '');

  // Scroll to the hash section on load and when the hash changes (the header
  // Company menu links to /company#careers, /company#support, /company#news).
  // Honours prefers-reduced-motion: a smooth scroll over a long page is one of
  // the motions that reliably triggers vestibular discomfort.
  useEffect(() => {
    const id = (location.hash || '').replace('#', '');
    if (!id) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash]);

  const eyebrowSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: ACCENT,
    mb: 1.5,
  };
  // One section rhythm for the whole page: same top margin, same top padding,
  // same hairline rule, same scroll offset. No section sets its own.
  const sectionSx = {
    mt: { xs: 5, md: 7 },
    pt: { xs: 4, md: 5 },
    borderTop: `1px solid ${hair}`,
    scrollMarginTop: { xs: '76px', md: '96px' },
  };
  const headingSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '1.5rem', md: '1.9rem' },
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: 'text.primary',
    mb: 1.25,
  };
  const bodySx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '0.9rem', md: '0.95rem' },
    lineHeight: 1.65,
    color: 'text.secondary',
  };
  const focusRing = {
    '&:focus-visible': {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 3,
    },
  };
  const darkBtn = {
    textTransform: 'none', fontFamily: BRAND_SANS_FONT, fontWeight: 500, fontSize: '0.92rem',
    borderRadius: '999px', px: 3.25, py: 1.1, backgroundColor: INK, color: '#FAF9F7', boxShadow: 'none',
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
    '&:hover': { backgroundColor: '#1A0A04', boxShadow: '0 4px 12px rgba(46, 20, 10, 0.22)' },
    '&:active': { transform: 'translateY(1px)', boxShadow: 'none' },
    ...focusRing,
  };
  const outlineBtn = {
    textTransform: 'none', fontFamily: BRAND_SANS_FONT, fontWeight: 500, fontSize: '0.92rem',
    borderRadius: '999px', px: 3.25, py: 1.1, color: 'text.primary', border: `1px solid ${alpha(INK, 0.18)}`,
    transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.12s ease',
    '&:hover': { backgroundColor: alpha(INK, 0.04), borderColor: alpha(INK, 0.3) },
    '&:active': { transform: 'translateY(1px)' },
    ...focusRing,
  };
  const inlineLinkSx = {
    color: ACCENT,
    textDecoration: 'none',
    fontWeight: 500,
    borderRadius: '4px',
    '&:hover': { textDecoration: 'underline' },
    ...focusRing,
  };

  const SectionHead = ({ id, eyebrow, title }) => (
    <Box>
      <Typography component="div" sx={eyebrowSx}>{eyebrow}</Typography>
      <Typography component="h2" id={`${id}-h`} sx={headingSx}>{title}</Typography>
    </Box>
  );

  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SEO
        title="Company | Restack"
        description="About Restack: founder-led, building the deliverable engine for Private Equity, an AI agent that produces client-ready models, decks, and memos in your live Office files. Careers, support, and the latest news."
        canonical="/company"
        schema={COMPANY_FAQ_SCHEMA}
      />

      <SiteHeader />

      <Box
        component="main"
        id="main-content"
        sx={{
          maxWidth: 1000,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 2.5rem)' },
          pb: { xs: 6, md: 9 },
        }}
      >
        {/* Hero. The headline names the audience and the outcome; the
            subheadline says who actually builds and answers. */}
        <CenteredHeader sx={{ maxWidth: 760, mx: 'auto' }}>
          <SectionEyebrow>Company</SectionEyebrow>
          <SectionTitle component="h1">
            Built by an operator, for <em>private equity</em>
          </SectionTitle>
          <SectionSubtitle sx={{ mx: 'auto', mb: 0 }}>
            Restack is the deliverable engine for private equity: an AI agent that produces
            client-ready decks, models, and memos in your live Office files. It is founder-led,
            so the person who builds the product is the person who runs your demo and answers
            your support email.
          </SectionSubtitle>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mt: { xs: 3, md: 3.5 } }}>
            <Button
              component={RouterLink}
              to="/demo"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
              sx={darkBtn}
            >
              See it on your own deck
            </Button>
            <Button component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={outlineBtn}>
              Email the founder
            </Button>
          </Box>
        </CenteredHeader>

        {/* Proof row. Three commitments already made elsewhere on the site,
            surfaced once so the page carries its own evidence. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 1.5, md: 2 },
            mt: { xs: 4, md: 5 },
          }}
        >
          {PROOF_POINTS.map((p) => (
            <Box
              key={p.stat}
              sx={{
                p: { xs: 2.25, md: 2.5 },
                borderRadius: '16px',
                border: `1px solid ${alpha(INK, 0.1)}`,
                backgroundColor: alpha(INK, 0.015),
              }}
            >
              <Typography
                component="div"
                sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '-0.015em', color: ACCENT, mb: 0.5 }}
              >
                {p.stat}
              </Typography>
              <Typography
                component="div"
                sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}
              >
                {p.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Section nav. Anchors, not page loads, so the current section is
            marked with aria-current for assistive tech as well as visually. */}
        <Box
          component="nav"
          aria-label="Company sections"
          sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: { xs: 4, md: 5 } }}
        >
          {SECTION_NAV.map((p) => {
            const isActive = activeHash === p.id;
            return (
              <Box
                key={p.id}
                component={RouterLink}
                to={`/company#${p.id}`}
                aria-current={isActive ? 'true' : undefined}
                sx={{
                  fontFamily: BRAND_SANS_FONT, fontSize: '0.82rem', fontWeight: 500,
                  color: isActive ? ACCENT : 'text.secondary',
                  textDecoration: 'none', px: 2, py: 0.75, borderRadius: '999px',
                  border: `1px solid ${isActive ? alpha(ACCENT, 0.35) : alpha(INK, 0.14)}`,
                  backgroundColor: isActive ? alpha(ACCENT, 0.06) : 'transparent',
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.12s ease',
                  '&:hover': { color: 'text.primary', backgroundColor: alpha(INK, 0.03), borderColor: alpha(INK, 0.24) },
                  '&:active': { transform: 'translateY(1px)' },
                  ...focusRing,
                }}
              >
                {p.label}
              </Box>
            );
          })}
        </Box>

        {/* FOUNDER - the pedigree is the trust asset, and the paragraph is the
            problem-to-solution beat for the whole page: the last mile stayed
            manual, so he built the thing that does it. Photo is the same
            headshot the /demo founder card uses. TODO(founder): append the
            last name once confirmed for public use. */}
        <Box component="section" id="founder" aria-labelledby="founder-h" sx={sectionSx}>
          <SectionHead id="founder" eyebrow="Founder" title="Built by an operator from the industry" />
          <Box sx={{ mt: 3, display: 'flex', gap: { xs: 2, md: 2.5 }, alignItems: 'flex-start', maxWidth: 720 }}>
            <Box
              component="img"
              src="/founder/harsh.jpg"
              alt="Harsh, founder of Restack"
              width={72}
              height={72}
              sx={{ width: { xs: 56, md: 72 }, height: { xs: 56, md: 72 }, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#E8E5E1' }}
            />
            <Box>
              <Typography sx={{ ...bodySx }}>
                Restack is built by Harsh - ex-Roland Berger, then product lead at an $8B PE fund. He
                built Restack because the last mile of every engagement - the deck, the model, the
                memo - stayed manual long after the research stopped being. Every demo, proof run, and
                support reply comes from him directly.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 1.5 }}>
                <Box
                  component="a"
                  href="https://www.linkedin.com/in/heyhiharsh/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ...inlineLinkSx, fontFamily: BRAND_SANS_FONT, fontSize: '0.85rem' }}
                >
                  Harsh on LinkedIn
                </Box>
                <Box
                  component="a"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  sx={{ ...inlineLinkSx, fontFamily: BRAND_SANS_FONT, fontSize: '0.85rem' }}
                >
                  {SUPPORT_EMAIL}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* CAREERS */}
        <Box component="section" id="careers" aria-labelledby="careers-h" sx={sectionSx}>
          <SectionHead id="careers" eyebrow="Careers" title="Build with Restack" />
          <Typography sx={{ ...bodySx, maxWidth: 680 }}>
            We want to meet engineers, designers, and go-to-market people set on turning the analyst week of model,
            deck, and memo work into something one AI agent does inside the user&rsquo;s own Office files.
          </Typography>
          <Box
            sx={{
              mt: 3, p: { xs: 3, md: 3.5 }, borderRadius: '16px', border: `1px solid ${alpha(INK, 0.1)}`,
              backgroundColor: '#ffffff', maxWidth: 620,
            }}
          >
            <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 600, color: 'text.primary', mb: 1 }}>
              No open roles listed right now.
            </Typography>
            <Typography sx={{ ...bodySx, mb: 2.5 }}>
              If building this is what you want to do, tell us what you would want to build and why.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <Button component="a" href={`mailto:${CAREERS_EMAIL}`} sx={outlineBtn}>Get in touch</Button>
              <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.82rem', color: alpha(INK, 0.5) }}>
                Or email{' '}
                <Box component="a" href={`mailto:${CAREERS_EMAIL}`} sx={inlineLinkSx}>
                  {CAREERS_EMAIL}
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* SUPPORT */}
        <Box component="section" id="support" aria-labelledby="support-h" sx={sectionSx}>
          <SectionHead id="support" eyebrow="Support" title="Get help with Restack" />
          <Typography sx={{ ...bodySx, maxWidth: 680 }}>
            For PowerPoint, Excel, or Word, email{' '}
            <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={inlineLinkSx}>{SUPPORT_EMAIL}</Box>{' '}
            with your account email, the app, the steps you took, and the live file. We reply within 24 hours.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, md: 2.5 }, mt: 3 }}>
            {supportTopics.map((t) => (
              <Box
                key={t.title}
                sx={{
                  p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: `1px solid ${alpha(INK, 0.1)}`,
                  backgroundColor: alpha(INK, 0.015), display: 'flex', gap: 2, alignItems: 'flex-start',
                }}
              >
                <Box aria-hidden="true" sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${alpha(ACCENT, 0.22)}`, backgroundColor: alpha(ACCENT, 0.07), color: ACCENT, '& svg': { fontSize: 20 } }}>
                  {t.icon}
                </Box>
                <Box>
                  <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.98rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>{t.title}</Typography>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}>{t.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={outlineBtn}>Email support</Button>
            <Button component={RouterLink} to="/settings" sx={outlineBtn}>Account settings</Button>
          </Box>
        </Box>

        {/* NEWS */}
        <Box component="section" id="news" aria-labelledby="news-h" sx={sectionSx}>
          <SectionHead id="news" eyebrow="News" title="What is new" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 2.5 }, mt: 1 }}>
            {newsUpdates.map((u) => (
              <Box
                key={u.title}
                sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '16px', border: `1px solid ${alpha(INK, 0.1)}`, backgroundColor: '#ffffff' }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Box sx={{ px: 1, py: 0.3, borderRadius: '999px', backgroundColor: alpha(ACCENT, 0.1), color: ACCENT, fontFamily: BRAND_SANS_FONT, fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{u.tag}</Box>
                  <Typography component="span" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', color: alpha(INK, 0.5) }}>{u.date}</Typography>
                </Box>
                <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '1rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.35, mb: 0.75 }}>{u.title}</Typography>
                <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}>{u.body}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Button
              component={RouterLink}
              to="/news"
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
              sx={outlineBtn}
            >
              See all news
            </Button>
          </Box>
        </Box>

        {/* FAQ - the same answers the support widget gives, pulled by id from
            FAQ_REPOSITORY and emitted as FAQPage JSON-LD via the SEO schema. */}
        <Box component="section" id="faq" aria-labelledby="faq-h" sx={sectionSx}>
          <SectionHead id="faq" eyebrow="FAQ" title="Questions we get before a first call" />
          <Box sx={{ mt: 2, maxWidth: 760 }}>
            {COMPANY_FAQ.map(({ id, question, answer }) => (
              <Accordion
                key={id}
                disableGutters
                elevation={0}
                square
                sx={{
                  backgroundColor: 'transparent',
                  borderBottom: `1px solid ${alpha(INK, 0.08)}`,
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded sx={{ color: alpha(INK, 0.45) }} />}
                  sx={{
                    px: 0.5,
                    minHeight: 52,
                    borderRadius: '8px',
                    '& .MuiAccordionSummary-content': { my: 1 },
                    '&:hover': { backgroundColor: alpha(INK, 0.02) },
                    ...focusRing,
                  }}
                >
                  {/* MUI v7 already wraps the summary in its own heading, so
                      this stays a span to avoid nesting a heading in a heading. */}
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: BRAND_SANS_FONT,
                      fontSize: '0.92rem',
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                  >
                    {question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                  <Typography sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.84rem', lineHeight: 1.6, color: 'text.secondary' }}>
                    {answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* FINAL CTA - the same primary action as the hero, repeated verbatim. */}
        <Box component="section" aria-labelledby="cta-h" sx={sectionSx}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '16px',
              textAlign: 'center',
              border: `1px solid ${alpha(INK, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.05)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
            }}
          >
            <Typography component="h2" id="cta-h" sx={{ ...headingSx, fontSize: { xs: '1.3rem', md: '1.5rem' }, mb: 1 }}>
              See it build on your own deck
            </Typography>
            <Typography sx={{ ...bodySx, maxWidth: 520, mx: 'auto', mb: 2.5 }}>
              Send your details and Harsh will reach out within a day to set up a time.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
              <Button
                component={RouterLink}
                to="/demo"
                endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
                sx={darkBtn}
              >
                See it on your own deck
              </Button>
              <Button component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={outlineBtn}>
                Email the founder
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />
    </LandingContainer>
  );
}
